import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import { useSelector } from "react-redux"
import { round100, HTtoTTC, formatNumber } from "../../utilities/Addons"
import { URL } from "../../store/actions/depannageActions"
import CustomModal from "../basics/interface/CustomModal"
import { COLORS } from "../../utilities/Constants"
import { StatRow } from "./AtelierSearchTotal"

const SectionLabel = ({ children }) => (
	<div
		style={{
			marginTop: 18,
			marginBottom: 6,
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.08em",
			textTransform: "uppercase",
			color: COLORS.textLight,
		}}
	>
		{children}
	</div>
)

// Calcul des marges sur l'ensemble des fiches du résultat : revenu (vente) et
// coût (achat) par catégorie. Les lignes issues d'un modèle délèguent leur coût
// au modèle (endpoint ateliermodelemarge).
const computeMarge = async (ateliers, token) => {
	let tmoh = 0,
		tmof = 0,
		cmof = 0,
		tff = 0,
		cff = 0,
		taf = 0,
		caf = 0
	const allModels = []
	ateliers.forEach((atelier) => {
		const models = []
		;(atelier.atelierLignes || []).forEach((aL) => {
			const hasModel = ![0, null].includes(aL.modele)
			if (hasModel && !models.includes(aL.modele)) models.push(aL.modele)
			let vente = aL.quantite * aL.prixVente
			if (aL.isHT) vente = HTtoTTC(vente, atelier.tva)
			let achat = aL.quantite * aL.prixAchat
			if (aL.isAchHT) achat = HTtoTTC(achat, atelier.tva)
			switch (aL.type) {
				case 0: // Fournitures
					tff += vente
					if (!hasModel) cff += achat
					break
				case 1: // Main d'œuvre
					tmoh += aL.quantite
					if (!hasModel) {
						tmof += vente
						cmof += achat
					}
					break
				case 2: // Autres
					taf += vente
					if (!hasModel) caf += achat
					break
				default:
					break
			}
		})
		allModels.push(...models)
	})
	const res = await fetch(`${URL}api/ateliermodelemarge`, {
		method: "post",
		headers: {
			Accept: "application/json, text/plain",
			"Content-type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(allModels),
	})
	const json = await res.json()
	;(Array.isArray(json) ? json : []).forEach((aL) => {
		let vente = aL.quantite * aL.prixVente
		if (aL.isHT) vente = HTtoTTC(vente, 7.7)
		let achat = aL.quantite * aL.prixAchat
		if (aL.isAchHT) achat = HTtoTTC(achat, 7.7)
		switch (aL.type) {
			case 0:
				cff += achat
				break
			case 1:
				tmof += vente
				cmof += achat
				break
			case 2:
				caf += achat
				break
			default:
				break
		}
	})
	return {
		totMainOeuvreHeures: round100(tmoh),
		totMainOeuvreFrancs: round100(tmof),
		coutMainOeuvreFrancs: round100(cmof),
		totFournitureFrancs: round100(tff),
		coutFournitureFrancs: round100(cff),
		totAutreFrancs: round100(taf),
		coutAutreFrancs: round100(caf),
	}
}

const AtelierSearchMarge = ({ visible, closeModal, ateliers }) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const [m, setM] = useState(null)

	useEffect(() => {
		let active = true
		computeMarge(ateliers, token)
			.then((res) => active && setM(res))
			.catch(() => active && setM({}))
		return () => {
			active = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (!visible) return null

	const margeMO = m ? round100(m.totMainOeuvreFrancs - m.coutMainOeuvreFrancs) : 0
	const margeFO = m ? round100(m.totFournitureFrancs - m.coutFournitureFrancs) : 0
	const margeAU = m ? round100(m.totAutreFrancs - m.coutAutreFrancs) : 0
	const margeTot = round100(margeMO + margeFO + margeAU)

	return (
		<CustomModal
			title="Marge"
			onClose={closeModal}
			showFooter={false}
			maxWidth={460}
		>
			{!m ? (
				<div style={{ display: "flex", justifyContent: "center", padding: 28 }}>
					<FontAwesomeIcon
						icon={faSync}
						spin
						style={{ color: COLORS.textLight, fontSize: 20 }}
					/>
				</div>
			) : (
				<div>
					<SectionLabel>Main d'œuvre</SectionLabel>
					<StatRow
						label="Total heures"
						value={formatNumber(m.totMainOeuvreHeures)}
						unit="Heures"
					/>
					<StatRow label="Coût" value={formatNumber(m.coutMainOeuvreFrancs)} />
					<StatRow label="Revenu" value={formatNumber(m.totMainOeuvreFrancs)} />
					<StatRow label="Marge" value={formatNumber(margeMO)} strong />

					<SectionLabel>Fournitures</SectionLabel>
					<StatRow label="Coût" value={formatNumber(m.coutFournitureFrancs)} />
					<StatRow label="Revenu" value={formatNumber(m.totFournitureFrancs)} />
					<StatRow label="Marge" value={formatNumber(margeFO)} strong />

					<SectionLabel>Autres</SectionLabel>
					<StatRow label="Coût" value={formatNumber(m.coutAutreFrancs)} />
					<StatRow label="Revenu" value={formatNumber(m.totAutreFrancs)} />
					<StatRow label="Marge" value={formatNumber(margeAU)} strong />

					<div style={{ marginTop: 18 }}>
						<StatRow
							label="Marge totale"
							value={formatNumber(margeTot)}
							strong
						/>
					</div>
				</div>
			)}
		</CustomModal>
	)
}

export default AtelierSearchMarge
